import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "../components/Seo";
import { certificates } from "../content";
import { breadcrumbSchema, certificateSchema } from "../lib/structuredData";
import NotFoundPage from "./NotFoundPage";

function CertificatePage() {
  const { slug } = useParams();
  const cert = certificates.find((item) => item.slug === slug);
  const [imageFailed, setImageFailed] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!cert) {
    return <NotFoundPage />;
  }

  const showImage = Boolean(cert.image) && !imageFailed;
  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/certificates/${cert.slug}`
      : `/certificates/${cert.slug}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="cert-page">
      <Seo
        description={`${cert.title} - certificate earned by Robert Doe via ${cert.issuer} on ${cert.date}. ${cert.description ?? ""}`.trim()}
        jsonLd={[
          certificateSchema(cert),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Certificates", path: "/certificates" },
            { name: cert.title, path: `/certificates/${cert.slug}` },
          ]),
        ]}
        path={`/certificates/${cert.slug}`}
        title={`${cert.title} - Certificate | Robert Doe`}
      />
      <div className="cert-page-card reveal visible">
        <Link className="cert-page-back" to="/certificates">
          Back to Certificates
        </Link>

        {showImage ? (
          <div className="cert-page-image-wrap">
            <img
              alt={`${cert.title} certificate`}
              className="cert-page-image"
              onError={() => setImageFailed(true)}
              src={cert.image}
            />
          </div>
        ) : (
          <div className="modal-icon">CERT</div>
        )}

        <h1 className="cert-page-title">{cert.title}</h1>
        <div className="cert-page-meta">
          <span>{cert.issuer}</span>
          <span aria-hidden="true">&middot;</span>
          <span>{cert.date}</span>
          {cert.hours ? (
            <>
              <span aria-hidden="true">&middot;</span>
              <span>{cert.hours} hrs</span>
            </>
          ) : null}
        </div>

        {cert.description ? (
          <p className="cert-page-description">{cert.description}</p>
        ) : null}

        {cert.tags && cert.tags.length ? (
          <div className="modal-tags">
            {cert.tags.map((tag) => (
              <span className="modal-tag" key={tag}>
                #{tag.replace(/\s+/g, "")}
              </span>
            ))}
          </div>
        ) : null}

        <div className="cert-page-share">
          <span className="cert-page-share-label">Credential URL</span>
          <div className="cert-page-share-row">
            <input
              aria-label="Credential URL"
              className="cert-page-share-input"
              onFocus={(event) => event.target.select()}
              readOnly
              type="text"
              value={shareUrl}
            />
            <button className="btn-secondary" onClick={handleCopy} type="button">
              {copied ? "Copied!" : "Copy Link"}
            </button>
          </div>
          <p className="cert-page-share-note">
            Pluralsight doesn&apos;t provide a public verification link for this
            certificate, so use this page as the credential URL (e.g. on LinkedIn).
          </p>
        </div>

        {cert.credentialUrl ? (
          <a
            className="btn-secondary"
            href={cert.credentialUrl}
            rel="noreferrer"
            target="_blank"
          >
            Verify Credential
          </a>
        ) : null}
      </div>
    </div>
  );
}

export default CertificatePage;
